import { openNewWindowWithDimensions, getViewerLogicalName } from './acrossTabsUtils'

const OHIF_BASE_URL = import.meta.env.VITE_OHIF_WEB_URL
export const OHIF_SITE_MODE = `${OHIF_BASE_URL}/preview`
export const OHIF_QC_MODE = `${OHIF_BASE_URL}/qc`
export const OHIF_READER_MODE = `${OHIF_BASE_URL}/blinded`
export const FUSION_READER_MODE = `${OHIF_BASE_URL}/blinded-tmtv`
export const OHIF_SEG_MODE = `${OHIF_BASE_URL}/seg`
export const OPHTHO_READER_MODE = `${OHIF_BASE_URL}/ophtho`

// New helper function specifically for generating the reader URL
export const getGcisViewerUrl = (
  taskId: string,
  studyId: string,
  index: number = 0, // default = blinded mode
  adjudicationTaskId?: string,
): string | null => {
  if (!taskId || !studyId) {
    console.error('getOhifReaderUrl: One or more required parameters missing')
    return null
  }
  let url
  switch (index) {
    case 0:
      url = new URL(OHIF_READER_MODE)
      break
    case 1:
      url = new URL(FUSION_READER_MODE)
      break
    case 2:
      url = new URL(OHIF_SEG_MODE)
      break
    case 3:
      url = new URL(OPHTHO_READER_MODE)
      break
    default:
      url = new URL(OHIF_SEG_MODE)
  }
  url.searchParams.append('studyId', studyId)
  url.searchParams.append('taskId', taskId)

  if (adjudicationTaskId) {
    url.searchParams.append('adjudicationTaskId', adjudicationTaskId)
  }

  return url.toString()
}

// Updated to use the helper and define logicalName
export const launchDicomViewerForReader = (
  taskId: string,
  studyId: string,
  index: number,
  adjudicationTaskId?: string,
): void => {
  console.log('adjudicationTaskId==>', adjudicationTaskId)
  const urlString = getGcisViewerUrl(taskId, studyId, index, adjudicationTaskId)
  if (!urlString) {
    // Error already logged by getOhifReaderUrl
    return // Stop if URL couldn't be generated
  }

  // Define the logical name based on the task ID (consistent with components)
  const logicalName = getViewerLogicalName(taskId, index)

  // Pass both the URL and the logical name to the window management utility
  openNewWindowWithDimensions(urlString, logicalName)
  // No need to return the childWindow proxy usually
}

export const launchDicomViewerForQC = (
  studyId: string,
  qcTaskId: string,
  _jwtToken: string,
  initialSeriesInstanceUID?: string,
  showSourceData: boolean = true,
  /** When true, also set SeriesInstanceUIDs so OHIF loads only that series */
  restrictToSeries: boolean = false,
  /** StudyInstanceUID of a single exam; combined with restrictToStudy it limits the viewer to that exam */
  initialStudyInstanceUID?: string,
  restrictToStudy: boolean = false,
) => {
  if (!qcTaskId || !studyId) {
    console.error('One of more required parameters missing')
    return
  }

  const url = new URL(OHIF_QC_MODE)
  url.searchParams.append('qcTaskId', qcTaskId)
  url.searchParams.append('studyId', studyId)
  url.searchParams.append('showSourceData', showSourceData.toString())
  if (initialSeriesInstanceUID) {
    url.searchParams.append('initialSeriesInstanceUID', initialSeriesInstanceUID)
    // Flag only - avoids duplicating the UID itself in the URL. The viewer
    // reads initialSeriesInstanceUID + this flag together to restrict to one series.
    if (restrictToSeries) {
      url.searchParams.append('restrictToSeries', 'true')
    }
  }
  if (initialStudyInstanceUID) {
    url.searchParams.append('initialStudyInstanceUID', initialStudyInstanceUID)
    if (restrictToStudy) {
      url.searchParams.append('restrictToStudy', 'true')
    }
  }

  window.open(url.toString(), '_blank', 'noopener,noreferrer')
}

export const launchDicomViewerForSite = (
  studyId: string,
  subjectId: string,
  timepointSubmissionId: string,
  containerSubmissionId: string,
  _jwtToken: string,
  initialSeriesInstanceUID?: string,
  /** When true, also set SeriesInstanceUIDs so OHIF loads only that series */
  restrictToSeries: boolean = false,
  /** StudyInstanceUID of a single exam; combined with restrictToStudy it limits the viewer to that exam */
  initialStudyInstanceUID?: string,
  restrictToStudy: boolean = false,
) => {
  if (!subjectId || !studyId || !timepointSubmissionId || !containerSubmissionId) {
    console.error('One of more required parameters missing')
    return
  }

  const url = new URL(OHIF_SITE_MODE)
  url.searchParams.append('studyId', studyId)
  url.searchParams.append('subjectId', subjectId)
  url.searchParams.append('timepointSubmissionId', timepointSubmissionId)
  url.searchParams.append('containerSubmissionId', containerSubmissionId)
  if (initialSeriesInstanceUID) {
    url.searchParams.append('initialSeriesInstanceUID', initialSeriesInstanceUID)
    // Flag only - avoids duplicating the UID itself in the URL. The viewer
    // reads initialSeriesInstanceUID + this flag together to restrict to one series.
    if (restrictToSeries) {
      url.searchParams.append('restrictToSeries', 'true')
    }
  }
  if (initialStudyInstanceUID) {
    url.searchParams.append('initialStudyInstanceUID', initialStudyInstanceUID)
    if (restrictToStudy) {
      url.searchParams.append('restrictToStudy', 'true')
    }
  }

  window.open(url.toString(), '_blank', 'noopener,noreferrer')
}

export const launchDicomViewerForSiteMultipleContainer = (
  studyId: string,
  subjectId: string,
  timepointSubmissionId: string,
  containerSubmissionIds: string[],
) => {
  if (!subjectId || !studyId || !timepointSubmissionId || containerSubmissionIds.length === 0) {
    console.error('One or more required parameters missing')
    return
  }

  const url = new URL(OHIF_SITE_MODE)
  url.searchParams.append('studyId', studyId)
  url.searchParams.append('subjectId', subjectId)
  url.searchParams.append('timepointSubmissionId', timepointSubmissionId)
  containerSubmissionIds.forEach((id) => url.searchParams.append('containerSubmissionId', id))

  window.open(url.toString(), '_blank', 'noopener,noreferrer')
}

export const launchDicomViewerForSiteMultipleTimepoints = (
  studyId: string,
  subjectId: string,
  pairs: Array<{
    timepointSubmissionId: string
    containerSubmissionId: string
    timepointName?: string
  }>,
) => {
  if (!studyId || !subjectId || pairs.length === 0) {
    console.error('launchDicomViewerForSiteMultipleTimepoints: missing parameters')
    return
  }

  const url = new URL(OHIF_SITE_MODE)
  url.searchParams.append('studyId', studyId)
  url.searchParams.append('subjectId', subjectId)
  pairs.forEach(({ timepointSubmissionId, containerSubmissionId, timepointName }, i) => {
    url.searchParams.append('timepointSubmissionId', timepointSubmissionId)
    url.searchParams.append('containerSubmissionId', containerSubmissionId)
    url.searchParams.append('timepointName', timepointName || `Timepoint ${i + 1}`)
  })

  window.open(url.toString(), '_blank', 'noopener,noreferrer')
}

export const launchDicomViewerForSegmentation = (
  studyId: string,
  qcTaskId: string,
  _jwtToken: string,
  initialSeriesInstanceUID?: string,
  showSourceData: boolean = true,
) => {
  if (!qcTaskId || !studyId) {
    console.error('One of more required parameters missing')
    return
  }

  const url = new URL(OHIF_SEG_MODE)
  url.searchParams.append('qcTaskId', qcTaskId)
  url.searchParams.append('studyId', studyId)
  url.searchParams.append('showSourceData', showSourceData.toString())
  if (initialSeriesInstanceUID) {
    url.searchParams.append('initialSeriesInstanceUID', initialSeriesInstanceUID)
  }

  window.open(url.toString(), '_blank', 'noopener,noreferrer')
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const composeOhifReaderLink = (task_id: string, study_id: string, _jwtToken: string) => {
  return `${OHIF_READER_MODE}?studyId=${study_id}&taskId=${task_id}`
}
